// Desktop sidebar — 260px, or a 64px icon rail the viewer chooses with the chevron
// at the top. Hidden below md breakpoint (the mobile nav takes over).
// Renders navigation groups from the registry. It sets its own width, so
// AdminLayout's grid column is `auto`.

import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeftRight, LockKeyhole, LogOut } from 'lucide-react'
import { ThemeToggle } from '@/components/ThemeToggle'
import { PassphrasePrompt } from '@/components/PassphrasePrompt'
import { SwitchRestaurantDialog } from '@/components/SwitchRestaurantDialog'
import { useCanSwitchRestaurant } from '@/hooks/useCanSwitchRestaurant'
import { useTeam } from '@/state/useTeam'
import { useAuth } from '@/state/useAuth'
import { useWatchMode } from '@/state/useWatchMode'
import { groups } from './navRegistry'
import { visibleGroups, isItemHidden } from '@synvya/portal-ui/visibility'
import { SidebarGroup, SidebarRailToggle, useSidebarRail } from '@synvya/portal-ui/sidebar'
import type { NavItem } from '@synvya/portal-ui/types'
import { useTranslation } from 'react-i18next'

const COLLAPSE_KEY_PREFIX = 'synvya_account_nav_collapsed:'
/** Where the viewer's full-or-rail choice is stored. */
export const RAIL_STORAGE_KEY = 'synvya_sidebar_rail_account'
/** An icon-only footer button in the rail. */
const RAIL_BUTTON =
  'flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground'
const hideItem = (i: NavItem) => isItemHidden(i) || Boolean(i.sidebarHidden)
// The loyalty root would otherwise stay active on every /app/loyalty/* page.
const matchLoyaltyRootExactly = (i: NavItem) => i.to === '/app/loyalty'

export function Sidebar() {
  const { t } = useTranslation()
  const { restaurantName, userRole } = useTeam()
  const canSwitch = useCanSwitchRestaurant()
  const [switching, setSwitching] = useState(false)
  const { logout, displayName, email, personalPubkey } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const startWatching = useWatchMode((s) => s.startWatching)
  const releaseWatch = useWatchMode((s) => s.releaseWatch)
  const [locking, setLocking] = useState(false)
  const userDisplayName = displayName ?? email ?? (personalPubkey ? `${personalPubkey.slice(0, 8)}…` : '—')
  const { rail, toggle } = useSidebarRail(RAIL_STORAGE_KEY)
  const shownName = restaurantName ?? 'Synvya Account'

  async function handleLogout() {
    // **Signing out is the documented way back from a forgotten passphrase**
    // (entry 6.25). A lock left behind would meet the next sign-in on this
    // device with a screen nobody holds the passphrase for.
    releaseWatch()
    await logout()
    navigate('/sign-in')
  }

  return (
    <aside
      data-rail={rail ? 'true' : 'false'}
      className={`hidden border-r border-border bg-card transition-[width] duration-200 md:flex md:min-h-0 md:flex-col ${
        rail ? 'md:w-16' : 'md:w-[260px]'
      }`}
    >
      {/* Top: the collapse chevron + restaurant name. The rail keeps an initial. */}
      {rail ? (
        <div className="flex flex-col items-center gap-2 border-b border-border px-2 py-3">
          <SidebarRailToggle rail={rail} onToggle={toggle} />
          <span
            title={shownName}
            aria-label={shownName}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
          >
            {shownName.charAt(0).toUpperCase()}
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-4">
          <span className="block min-w-0 truncate text-sm font-semibold text-foreground">{shownName}</span>
          <SidebarRailToggle rail={rail} onToggle={toggle} />
        </div>
      )}

      <nav
        className="min-h-0 flex-1 overflow-y-auto px-3 py-4"
        aria-label="Primary"
      >
        {visibleGroups(groups).map((group, i) => (
          <div
            key={group.id}
            className={
              i === 0
                ? 'pb-2'
                : 'mt-2 border-t border-border/60 py-2'
            }
          >
            <SidebarGroup
              group={group}
              renderLabel={(k) => t(`common:${k}`)}
              storageKeyPrefix={COLLAPSE_KEY_PREFIX}
              matchEnd={matchLoyaltyRootExactly}
              isItemHidden={hideItem}
              rail={rail}
            />
          </div>
        ))}
      </nav>

      {/* Footer: user info + role + theme + sign out */}
      {rail ? (
        <div className="flex flex-col items-center gap-1 border-t border-border px-2 py-3">
          <span
            data-testid="sidebar-user"
            title={userRole ? `${userDisplayName} (${userRole})` : userDisplayName}
            aria-label={userRole ? `${userDisplayName} (${userRole})` : userDisplayName}
            className="mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium text-foreground"
          >
            {userDisplayName.charAt(0).toUpperCase()}
          </span>
          {canSwitch && (
            <button
              onClick={() => setSwitching(true)}
              className={RAIL_BUTTON}
              title="Switch account"
              aria-label="Switch account"
            >
              <ArrowLeftRight className="h-4 w-4" aria-hidden />
            </button>
          )}
          <button
            onClick={() => setLocking(true)}
            className={RAIL_BUTTON}
            title="Watch this screen"
            aria-label="Watch this screen"
          >
            <LockKeyhole className="h-4 w-4" aria-hidden />
          </button>
          <ThemeToggle iconOnly />
          <button onClick={handleLogout} className={RAIL_BUTTON} title="Sign out" aria-label="Sign out">
            <LogOut className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : (
      <div className="space-y-3 border-t border-border p-4 text-sm">
        {/* Logged-in user */}
        <div data-testid="sidebar-user">
          <p className="truncate text-sm font-medium text-foreground">
            {userDisplayName}
          </p>
          {userRole && (
            <span className="mt-0.5 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
              {userRole}
            </span>
          )}
        </div>

        {canSwitch && (
          <button
            onClick={() => setSwitching(true)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ArrowLeftRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Switch account</span>
          </button>
        )}

        {/* Watch mode, at the bottom of the nav per entry 6.25. It locks on
            whatever panel is open, so it belongs with the account controls
            rather than inside any one group. */}
        <button
          onClick={() => setLocking(true)}
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <LockKeyhole className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Watch this screen</span>
        </button>

        <div className="flex items-center justify-between">
          <ThemeToggle />
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 rounded-md p-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      )}

      <SwitchRestaurantDialog open={switching} onClose={() => setSwitching(false)} />

      <PassphrasePrompt
        open={locking}
        title="Watch this screen"
        body={`This device will show ${pathname} and nothing else. Set a passphrase to unlock it again.`}
        confirmLabel="Start watching"
        requireConfirmation
        onCancel={() => setLocking(false)}
        onSubmit={async (passphrase) => {
          // Locked on the path without its query string: the board's service
          // day and tab live there, and a lock pinned to one of them would
          // stop a merchant changing the day on the screen they locked.
          const written = await startWatching(pathname, passphrase)
          if (written) setLocking(false)
          return written ? null : 'This browser would not store the lock, so the screen was not locked.'
        }}
      />
    </aside>
  )
}
